import { QueryObserverResult, RefetchOptions, RefetchQueryFilters, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { FC, createContext, ReactNode, useState, useEffect } from "react";
import { accountsService } from "../services/accounts";
import { apiInstance } from "../services/api";
import { cashManagementService } from "../services/cash-management";
import { projectsService } from "../services/projects";
import { Account } from "../types/account";
import { Project } from "../types/admin-management";
import { GLCode } from "../types/cash-management";
import { useAuth } from "./AuthContext";
import { drawRequestsService } from "../services/draw-request";
import { Approver } from "../types/approvers";
import { PaymentMethod } from "../types/payment-method";
import { userManagementService } from "../services/user-management";

interface ContextProps {
  accounts: Account[];
  loading: boolean;
  signatureContext: {
    signature?: string;
    setSignature: React.Dispatch<React.SetStateAction<string | undefined>>;
  };
  codes: GLCode[];
  startDate: moment.Moment;
  reloadAccounts: () => void;
  setStartDate: React.Dispatch<React.SetStateAction<moment.Moment>>;
  projects: (Project & { accountName?: string })[];
  refetchProjects: () => void;
  areProjectsLoading: boolean;
  drawRequestContext: {
    drawRequestData: {
      approvers: Approver[];
      reviewers: Approver[];
      expatOfficers: Approver[];
      paymentMethods: PaymentMethod[];
    };
    isDrawRequestDataLoading: boolean;
    isDrawRequestDataFetching: boolean;
    refetchDrawRequestData: () => void;
  };
  // userExtraInfo: Approver | undefined;
}

interface Props {
  children?: ReactNode;
}

export const DataContext = createContext<ContextProps>({
  signatureContext: {
    signature: undefined,
    setSignature: () => {}
  },
  codes: [],
  accounts: [],
  loading: true,
  reloadAccounts: () => {},
  startDate: moment(),
  setStartDate: () => {},
  projects: [],
  refetchProjects: () => {},
  areProjectsLoading: true,
  drawRequestContext: {
    drawRequestData: {
      reviewers: [],
      approvers: [],
      expatOfficers: [],
      paymentMethods: []
    },
    isDrawRequestDataFetching: true,
    isDrawRequestDataLoading: true,
    refetchDrawRequestData: () => {}
  }
  // userExtraInfo: undefined
});

export const DataContextProvider: FC<Props> = ({ children }) => {
  const { user } = useAuth();
  const hasTreasuryGroup = user.accountGroups.filter((item: string) => item === "Treasury").length;
  const [signature, setSignature] = useState<string | undefined>(undefined);
  // const [userExtraInfoAux, setUserExtraInfoAux] = useState<Approver | undefined>(undefined);
  //Queries

  const {
    isLoading: loadingAccounts,
    data: accounts,
    refetch
  } = useQuery<Account[]>(["accounts"], () => accountsService.getAllAccounts(), {
    initialData: [],
    enabled: !!apiInstance.defaults.headers.common["Authorization"]
  });
  const {
    isLoading: isLoadingSignature,
    data: signatureData,
    refetch: refetchSignature
  } = useQuery<string>(["signature"], () => userManagementService.getSignature(), {
    initialData: undefined,
    enabled: !!apiInstance.defaults.headers.common["Authorization"]
  });

  const {
    data: projects,
    isLoading: areProjectsLoading,
    isFetching,
    refetch: refetchProj
  } = useQuery(["projects"], () => projectsService.getAllProjects(true), {
    initialData: [],
    select: (data) => {
      return data.map((item) => {
        if (item.accountID) {
          const { accountNumber, accountName } = accounts?.filter((account) => item.accountID === account.accountID)[0];
          return { ...item, accountName: `${accountName} (${accountNumber})` };
        } else {
          return item;
        }
      });
    },
    enabled: !!accounts.length
  });
  const {
    data: drawRequestDataQuery,
    isLoading: isDrawRequestDataLoading,
    isFetching: isDrawRequestDataFetching,
    refetch: refetchDrawRequestData
  } = useQuery(["DrawRequestData"], () => drawRequestsService.getFormOptions());

  const { data: codes } = useQuery(["gl-codes"], () => cashManagementService.getGLCodes(), { initialData: [] });

  const [startDate, setStartDate] = useState(moment());

  const reloadAccounts = () => refetch();
  const refetchProjects = () => refetchProj();
  const [drawRequestData, setDrawRequestData] = useState<{
    approvers: Approver[];
    reviewers: Approver[];
    expatOfficers: Approver[];
    paymentMethods: PaymentMethod[];
  }>({ approvers: [], expatOfficers: [], paymentMethods: [], reviewers: [] });

  useEffect(() => {
    setDrawRequestData(() => {
      if (drawRequestDataQuery) return drawRequestDataQuery;
      else return { approvers: [], expatOfficers: [], paymentMethods: [], reviewers: [] };
    });

    // setUserExtraInfoAux(() =>
    //   [...(drawRequestDataQuery?.approvers || []), ...(drawRequestDataQuery?.expatOfficers || [])].find((ap) => ap.email === user.username)
    // );
  }, [drawRequestDataQuery]);

  useEffect(() => {
    setSignature(() => (!signatureData?.includes("data:image/png;base64,") ? `data:image/png;base64,${signatureData}` : signatureData));
  }, [signatureData]);

  return (
    <DataContext.Provider
      value={{
        signatureContext: {
          signature,
          setSignature
        },
        drawRequestContext: { drawRequestData, isDrawRequestDataLoading, isDrawRequestDataFetching, refetchDrawRequestData },
        accounts,
        codes,
        loading: loadingAccounts,
        reloadAccounts,
        startDate,
        setStartDate,
        refetchProjects,
        projects,
        areProjectsLoading: areProjectsLoading || isFetching
        // userExtraInfo: userExtraInfoAux
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
